import React, { useState } from "react";
import { CircleHelp, Lightbulb, X } from "lucide-react";
import { openHelp } from "../../utils/helpBus";

const keyFor = (id) => `khronos_tip_${id}`;

const seen = (id) => {
  try {
    return localStorage.getItem(keyFor(id)) === "1";
  } catch {
    return false;
  }
};

/**
 * A one-time tip the first time someone reaches a screen: a sentence or a
 * few bullets, "Learn more" into the help article, and "Got it". Never a
 * multi-step tour, and it never comes back once dismissed.
 *
 * `line`: a single sentence under a control instead of a card, with a "?"
 * into the help article and a cross to dismiss, so it never pushes what's
 * below it off the screen.
 */
const FirstVisitTip = ({ id, title, children, topic, line = false }) => {
  const [hidden, setHidden] = useState(() => seen(id));
  if (hidden) return null;

  const dismiss = () => {
    setHidden(true);
    try {
      localStorage.setItem(keyFor(id), "1");
    } catch {
      /* storage disabled: the tip may show again next visit */
    }
  };

  if (line) {
    return (
      <aside
        aria-label={title}
        className="flex items-center gap-2 text-[13px] leading-snug text-muted-foreground"
      >
        <Lightbulb className="h-4 w-4 shrink-0 text-sky-300" />
        <span className="min-w-0 flex-1">{children}</span>
        {topic && (
          <button
            type="button"
            aria-label={`Help: ${title}`}
            onClick={() => openHelp(topic)}
            className="grid h-8 w-8 shrink-0 place-items-center rounded-full hover:text-foreground"
          >
            <CircleHelp className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          aria-label="Dismiss tip"
          onClick={dismiss}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </aside>
    );
  }

  return (
    <aside
      aria-label={title}
      className="grid gap-2 rounded-2xl border border-sky-400/30 bg-sky-400/[0.07] p-3.5 text-left"
    >
      <p className="flex items-center gap-2 text-sm font-semibold text-white">
        <Lightbulb className="h-4 w-4 shrink-0 text-sky-300" />
        {title}
      </p>
      <div className="text-[13px] leading-relaxed text-foreground/85">{children}</div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={dismiss}
          className="rounded-lg bg-white/10 px-3 py-1.5 text-sm font-semibold text-white hover:bg-white/15"
        >
          Got it
        </button>
        {topic && (
          <button
            type="button"
            onClick={() => {
              dismiss();
              openHelp(topic);
            }}
            className="rounded-lg px-3 py-1.5 text-sm text-sky-300 hover:underline"
          >
            Learn more
          </button>
        )}
      </div>
    </aside>
  );
};

export default FirstVisitTip;
